import api from '../api';
import globalCache from './cache';

/**
 * Cached API wrapper that uses the global cache utility
 */
class CachedAPI {
  constructor() {
    this.isDebug = (typeof window !== 'undefined' && window.DEBUG_MODE) || 
                   (import.meta.env && import.meta.env.VITE_DEBUG_MODE === 'true');
  }

  /**
   * Make a cached GET request
   * @param {string} url - Request URL
   * @param {Object} options - Request options
   * @param {number} cacheTTL - Cache time to live in milliseconds (optional)
   * @returns {Promise} Response data
   */
  async get(url, options = {}, cacheTTL) {
    const cacheKey = globalCache.generateKey(url, { ...options, method: 'GET' });
    
    // Try to get from cache first
    const cachedData = globalCache.get(cacheKey);
    if (cachedData) {
      if (this.isDebug) {
        console.log('[CachedAPI] Cache hit for:', url);
      }
      // Add metadata to indicate this came from cache
      const responseWithMeta = { ...cachedData, _fromCache: true };
      return Promise.resolve(responseWithMeta);
    }

    if (this.isDebug) {
      console.log('[CachedAPI] Cache miss, fetching:', url);
    }

    try {
      const response = await api.get(url, options);
      
      // Store in cache
      globalCache.set(cacheKey, response, cacheTTL);
      
      if (this.isDebug) {
        console.log('[CachedAPI] Cached response for:', url);
      }
      
      // Add metadata to indicate this came from server
      response._fromCache = false;
      return response;
    } catch (error) {
      if (this.isDebug) {
        console.error('[CachedAPI] Request failed:', url, error);
      }
      throw error;
    }
  }

  /**
   * Make a POST request (not cached)
   * @param {string} url - Request URL
   * @param {*} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async post(url, data, options = {}) {
    return api.post(url, data, options);
  }

  /**
   * Make a PUT request (not cached)
   * @param {string} url - Request URL
   * @param {*} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async put(url, data, options = {}) {
    return api.put(url, data, options);
  }

  /**
   * Make a DELETE request (not cached)
   * @param {string} url - Request URL
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async delete(url, options = {}) {
    return api.delete(url, options);
  }

  /**
   * Clear cache for a specific URL pattern
   * @param {string} urlPattern - URL pattern to match
   */
  invalidateCache(urlPattern) {
    const keysToDelete = [];
    
    // Find all keys that match the pattern
    for (const [key] of globalCache.cache.entries()) {
      try {
        const keyObj = JSON.parse(key);
        if (keyObj.url.includes(urlPattern)) {
          keysToDelete.push(key);
        }
      } catch {
        // Invalid key format, skip
      }
    }

    // Delete matching keys
    keysToDelete.forEach(key => globalCache.delete(key));
    
    if (this.isDebug) {
      console.log('[CachedAPI] Invalidated cache for pattern:', urlPattern, 'Keys removed:', keysToDelete.length);
    }
  }

  /**
   * Clear all cache
   */
  clearCache() {
    globalCache.clear();
    if (this.isDebug) {
      console.log('[CachedAPI] Cleared all cache');
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache stats
   */
  getCacheStats() {
    return globalCache.getStats();
  }

  /**
   * Cleanup expired cache entries
   * @returns {number} Number of cleaned up entries
   */
  cleanupCache() {
    return globalCache.cleanup();
  }
}

// Export singleton instance
const cachedAPI = new CachedAPI();
export default cachedAPI;

// Export class for custom instances
export { CachedAPI };
